
<template>
  <Content>
    <div class="cart-page py-4">
      <!-- عنوان صفحه -->
      <div class="d-flex align-items-center justify-content-between mb-4">
        <h4 class="fw-bold text-teal m-0 d-flex align-items-center gap-2">
          <i class="bi bi-bag-heart-fill fs-3 text-pink"></i>
          {{ currentLang.title }}
        </h4>

        <button
          v-if="serverItems.length > 0"
          type="button"
          class="btn btn-outline-danger btn-sm rounded-pill px-3"
          @click="handleClearCart"
        >
          <i class="bi bi-trash3 me-1"></i>
          {{ currentLang.clearCart }}
        </button>
      </div>

      <!-- لودینگ -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-teal" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- سبد خرید خالی -->
      <div
        v-else-if="serverItems.length === 0"
        class="empty-cart-box bg-white rounded-4 shadow-sm text-center py-5 px-4"
      >
        <div class="empty-icon-wrapper mb-3">
          <i class="bi bi-cart-x text-teal"></i>
        </div>

        <h5 class="fw-bold text-dark mb-2">
          {{ currentLang.emptyCartTitle }}
        </h5>

        <p class="text-muted mb-4">
          {{ currentLang.emptyCartSub }}
        </p>

        <a
          :href="currentLang.urls.Shop"
          class="btn btn-teal-kids rounded-pill px-4 py-2 shadow-sm fw-bold"
        >
          <i class="bi bi-shop me-2"></i>
          {{ currentLang.backToShop }}
        </a>
      </div>

      <!-- محتوای سبد -->
      <div v-else class="row g-4">
        <!-- لیست کارت محصولات -->
        <div class="col-12 col-lg-8">
          <div class="cart-items-list">
            <article
              v-for="item in serverItems"
              :key="item.product_id"
              class="product-cart-card"
            >
              <!-- اطلاعات اصلی محصول -->
              <div class="product-card-main">
                <!-- تصویر محصول -->
                <div class="product-card-image">
                  <img
                    v-if="item.product?.image"
                    :src="item.product.image"
                    :alt="
                      lang === 'fa'
                        ? item.product?.name_fa
                        : item.product?.name_en
                    "
                    @error="handleImageError"
                  />

                  <div v-else class="product-card-image-placeholder">
                    <i class="bi bi-image"></i>
                  </div>
                </div>

                <!-- نام، دسته‌بندی و قیمت واحد -->
                <div class="product-card-content">
                  <div class="product-card-title-row">
                    <a
                      class="product-card-title"
                      :href="`${currentLang.urls.Product}/${
                        lang === 'fa'
                          ? item.product?.slug_fa
                          : item.product?.slug_en
                      }`"
                    >
                      {{
                        lang === "fa"
                          ? item.product?.name_fa
                          : item.product?.name_en
                      }}
                    </a>

                    <button
                      type="button"
                      class="product-card-delete"
                      :aria-label="lang === 'fa' ? 'حذف محصول' : 'Remove item'"
                      @click="removeItem(item.product_id)"
                    >
                      <i class="bi bi-trash3"></i>
                    </button>
                  </div>

                  <div
                    v-if="item.product?.category"
                    class="product-card-category"
                  >
                    {{ item.product.category }}
                  </div>

                  <div class="product-card-price">
                    <span>{{ currentLang.unitPrice }}:</span>

                    <strong>
                      {{ formatPrice(item.price) }}
                    </strong>
                  </div>
                </div>
              </div>

              <!-- جزئیات محصول -->
              <div class="product-card-details">
                <!-- موجودی -->
                <div class="product-card-detail">
                  <span class="product-card-detail-label">
                    <i class="bi bi-box-seam"></i>
                    {{ currentLang.stock }}
                  </span>

                  <strong
                    :class="
                      Number(item.quantity) >= Number(item.product?.stock || 0)
                        ? 'text-danger'
                        : 'text-success'
                    "
                  >
                    {{
                      Number(item.product?.stock || 0).toLocaleString(
                        lang === "fa" ? "fa-IR" : "en-US"
                      )
                    }}
                  </strong>
                </div>

                <!-- تعداد -->
                <div class="product-card-detail">
                  <span class="product-card-detail-label">
                    <i class="bi bi-123"></i>
                    {{ currentLang.quantity }}
                  </span>

                  <div class="product-card-quantity">
                    <button
                      type="button"
                      class="quantity-button"
                      @click="updateQuantity(item, Number(item.quantity) - 1)"
                    >
                      <i class="bi bi-dash"></i>
                    </button>

                    <span class="quantity-value">
                      {{
                        Number(item.quantity).toLocaleString(
                          lang === "fa" ? "fa-IR" : "en-US"
                        )
                      }}
                    </span>

                    <button
                      type="button"
                      class="quantity-button"
                      :disabled="
                        Number(item.product?.stock || 0) <= 0 ||
                        Number(item.quantity) >=
                          Number(item.product?.stock || 0)
                      "
                      @click="updateQuantity(item, Number(item.quantity) + 1)"
                    >
                      <i class="bi bi-plus"></i>
                    </button>
                  </div>
                </div>

                <!-- تخفیف -->
                <div
                  v-if="Number(item.total_discount || 0) > 0"
                  class="product-card-detail"
                >
                  <span class="product-card-detail-label">
                    <i class="bi bi-percent"></i>
                    {{ currentLang.discount }}
                  </span>

                  <strong class="text-danger">
                    - {{ formatPrice(item.total_discount) }}
                  </strong>
                </div>

                <!-- مالیات -->
                <div
                  v-if="Number(item.tax || 0) > 0"
                  class="product-card-detail"
                >
                  <span class="product-card-detail-label">
                    <i class="bi bi-receipt"></i>
                    {{ currentLang.tax }}
                  </span>

                  <strong>
                    {{ formatPrice(item.tax) }}
                  </strong>
                </div>
              </div>

              <!-- مبلغ نهایی محصول -->
              <div class="product-card-total">
                <span>{{ currentLang.itemTotal }}</span>

                <strong>
                  {{ formatPrice(item.total) }}
                </strong>
              </div>
            </article>
          </div>
        </div>

        <!-- خلاصه سفارش -->
        <div class="col-12 col-lg-4">
          <div
            class="summary-card card border-0 shadow-sm rounded-4 sticky-top"
          >
            <div class="card-body p-4">
              <h5
                class="fw-bold text-dark mb-4 pb-2 border-bottom d-flex align-items-center gap-2"
              >
                <i class="bi bi-receipt text-teal"></i>
                {{ currentLang.summaryTitle }}
              </h5>

              <!-- مبلغ کالاها -->
              <div class="summary-row">
                <span>{{ currentLang.subtotal }}</span>

                <strong>
                  {{ formatPrice(orderSummary.subtotal) }}
                </strong>
              </div>

              <!-- مالیات -->
              <div class="summary-row">
                <span>{{ currentLang.tax }}</span>

                <strong>
                  {{ formatPrice(orderSummary.tax) }}
                </strong>
              </div>

              <!-- هزینه ارسال -->
              <div class="summary-row">
                <span>{{ currentLang.shippingCost }}</span>

                <span
                  v-if="Number(orderSummary.shipping_cost || 0) === 0"
                  class="badge bg-success-subtle text-success rounded-pill px-3 py-1"
                >
                  {{ currentLang.freeShipping }}
                </span>

                <strong v-else>
                  {{ formatPrice(orderSummary.shipping_cost) }}
                </strong>
              </div>

              <!-- تخفیف -->
              <div
                v-if="Number(orderSummary.discount || 0) > 0"
                class="summary-row text-danger"
              >
                <span>{{ currentLang.discount }}</span>

                <strong> - {{ formatPrice(orderSummary.discount) }} </strong>
              </div>

              <!-- تخفیف روش پرداخت -->
              <div
                v-if="Number(orderSummary.discount_payment_method || 0) > 0"
                class="summary-row text-danger"
              >
                <span>
                  {{ currentLang.discountPaymentMethod }}
                </span>

                <strong>
                  -
                  {{ formatPrice(orderSummary.discount_payment_method) }}
                </strong>
              </div>

              <!-- مجموع تخفیف -->
              <div
                v-if="Number(orderSummary.total_discount || 0) > 0"
                class="summary-row text-danger fw-bold"
              >
                <span>{{ currentLang.totalDiscount }}</span>

                <strong>
                  - {{ formatPrice(orderSummary.total_discount) }}
                </strong>
              </div>

              <hr class="my-3 opacity-25" />

              <!-- مبلغ قابل پرداخت -->
              <div class="summary-total">
                <span>{{ currentLang.payableAmount }}</span>

                <strong>
                  {{ formatPrice(orderSummary.total_price) }}
                </strong>
              </div>
              <!-- جایگزین دکمه قبلی در خلاصه سفارش -->
              <button
                type="button"
                class="btn btn-teal-kids w-100 py-3 rounded-pill fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
                @click="handleProceedToCheckout"
              >
                <span>{{ currentLang.proceedToCheckout }}</span>

                <i
                  :class="
                    lang === 'fa' ? 'bi bi-arrow-left' : 'bi bi-arrow-right'
                  "
                ></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <footerAdmin></footerAdmin>
  </Content>
  <AuthModal v-model="showAuthModal" @success="handleLoginSuccess" />
</template>




<script setup>
import Content from "@/components/AdminContent.vue";
import footerAdmin from "@/components/AdminFooter.vue";
import { ref, onMounted, computed } from "vue";
import api from "@/plugins/axios";
import { useCart } from "@/stores/cart";
import { lang } from "@/plugins/lang";

import { useRouter } from "vue-router";
import AuthModal from "@/components/AuthModal.vue";

import enText from "@/locales/en/cart";
import faText from "@/locales/fa/cart";

const translations = { en: enText, fa: faText };
const currentLang = computed(() => translations[lang.value]);

const { items: localItems, addToCart, removeFromCart } = useCart();

const loading = ref(false);


const router = useRouter();

const showAuthModal = ref(false);
const authToken = ref(localStorage.getItem("auth_token"));
const userInfo = ref(null);

try {
  const savedUser = localStorage.getItem("user_info");
  userInfo.value = savedUser ? JSON.parse(savedUser) : null;
} catch (error) {
  localStorage.removeItem("user_info");
}

const isLoggedIn = computed(() => {
  return Boolean(authToken.value && userInfo.value);
});

// هدایت یا باز کردن مدال بر اساس وضعیت لاگین
const handleProceedToCheckout = () => {
  if (isLoggedIn.value) {
    router.push(currentLang.value.urls.Shipping);
  } else {
    showAuthModal.value = true;
  }
};

// دریافت اطلاعات پس از لاگین موفق و هدایت خودکار به تسویه حساب
const handleLoginSuccess = () => {
  authToken.value = localStorage.getItem("auth_token");
  try {
    const savedUser = localStorage.getItem("user_info");
    userInfo.value = savedUser ? JSON.parse(savedUser) : null;
  } catch (error) {
    userInfo.value = null;
  }

  showAuthModal.value = false;
  router.push(currentLang.value.urls.Shipping);
};


/*
|--------------------------------------------------------------------------
| داده‌های سبد — همه از ریسپانسیو ordercheck خوانده می‌شوند
|--------------------------------------------------------------------------
*/
const serverItems = ref([]);
const orderSummary = ref({
  subtotal: 0,
  tax: 0,
  shipping_cost: 0,
  discount: 0,
  discount_payment_method: 0,
  total_discount: 0,
  total_price: 0,
  items: [],
});

const syncOrderCheck = async () => {
  if (!localItems.value || localItems.value.length === 0) {
    serverItems.value = [];
    orderSummary.value = {
      subtotal: 0,
      tax: 0,
      shipping_cost: 0,
      discount: 0,
      discount_payment_method: 0,
      total_discount: 0,
      total_price: 0,
      items: [],
    };
    return;
  }

  loading.value = true;
  try {
    const response = await api.post("general/order/check", {
      items: localItems.value.map((item) => ({
        product_id: item.product_id || item.id,
        quantity: item.quantity,
      })),
    });

    const data = response.data?.data || response.data;

    if (data) {
      serverItems.value = data.items || [];
      // eslint-disable-next-line
      const { items, ...summary } = data;
      orderSummary.value = { ...orderSummary.value, ...summary, items: [] };
    }
  } catch (error) {
    console.error("Ordercheck sync error:", error);
  } finally {
    loading.value = false;
  }
};

/*
|--------------------------------------------------------------------------
| تغییر تعداد / حذف / خالی کردن
|--------------------------------------------------------------------------
*/
const updateQuantity = (item, newQty) => {
  if (newQty <= 0) {
    removeItem(item.product_id);
    return;
  }
  if (item.product?.stock && newQty > item.product.stock) {
    alert(`${currentLang.value.stockLimit}: ${item.product.stock}`);
    return;
  }

  addToCart(
    item.product_id,
    newQty - item.quantity,
    item.product?.stock,
    item.price
  );
  syncOrderCheck();
};

const removeItem = (productId) => {
  removeFromCart(productId);
  syncOrderCheck();
};

const handleClearCart = () => {
  const ids = serverItems.value.map((i) => i.product_id);
  ids.forEach((id) => removeFromCart(id));
  syncOrderCheck();
};

/*
|--------------------------------------------------------------------------
| توابع کمکی
|--------------------------------------------------------------------------
*/
const formatPrice = (price) => {
  if (price === null || price === undefined)
    return `۰ ${currentLang.value?.toman || "تومان"}`;

  return `${Number(price).toLocaleString(
    lang.value === "fa" ? "fa-IR" : "en-US"
  )} ${currentLang.value?.toman || "تومان"}`;
};

const handleImageError = (e) => {
  e.target.style.display = "none";
};

onMounted(() => {
  syncOrderCheck();
});
</script>

<style scoped>
/* پالت رنگی و متغیرها */
:root {
  --teal-primary: #0d9488;
  --teal-hover: #0f766e;
  --pink-accent: #ec4899;
}

.text-teal {
  color: #0d9488 !important;
}

.text-pink {
  color: #ec4899 !important;
}

.btn-teal-kids {
  background-color: #0d9488;
  border-color: #0d9488;
  color: #fff;
  transition: all 0.2s ease-in-out;
}

.btn-teal-kids:hover {
  background-color: #0f766e;
  border-color: #0f766e;
  color: #fff;
}

/* صفحه و وضعیت خالی */
.cart-page {
  margin: 10px;
  min-height: 60vh;
}

.empty-icon-wrapper i {
  font-size: 4.5rem;
}

/* کارت‌های محصول */
.cart-items-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.product-cart-card {
  background: #ffffff;
  border-radius: 1rem;
  padding: 1.25rem;
  border: 1px solid #f1f5f9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transition: box-shadow 0.2s ease;
}

.product-cart-card:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.07);
}

/* بخش اصلی کارت: عکس و اطلاعات */
.product-card-main {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.product-card-image {
  width: 90px;
  height: 90px;
  min-width: 90px;
  border-radius: 0.75rem;
  overflow: hidden;
  background-color: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
}

.product-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-card-image-placeholder i {
  font-size: 2rem;
  color: #cbd5e1;
}

.product-card-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.product-card-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.product-card-title {
  font-weight: 600;
  color: #1e293b;
  text-decoration: none;
  font-size: 1rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-card-title:hover {
  color: #0d9488;
}

.product-card-delete {
  background: none;
  border: none;
  color: #94a3b8;
  padding: 0.25rem;
  line-height: 1;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
}

.product-card-delete:hover {
  color: #ef4444;
  background-color: #fee2e2;
}

.product-card-category {
  font-size: 0.82rem;
  color: #64748b;
}

.product-card-price {
  font-size: 0.88rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.product-card-price strong {
  color: #0f172a;
  font-size: 0.95rem;
}

/* بخش جزئیات محصول (گرید ستون‌ها) */
.product-card-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 0.75rem;
  background: #f8fafc;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
}

.product-card-detail {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
}

.product-card-detail-label {
  color: #64748b;
  font-size: 0.78rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

/* کنترل تعداد */
.product-card-quantity {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 9999px;
  padding: 0.15rem 0.4rem;
  width: fit-content;
}

.quantity-button {
  background: none;
  border: none;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #334155;
  transition: background 0.15s ease;
}

.quantity-button:hover:not(:disabled) {
  background-color: #e2e8f0;
}

.quantity-button:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.quantity-value {
  min-width: 22px;
  text-align: center;
  font-weight: 600;
  font-size: 0.9rem;
}

/* جمع نهایی کارت تکی */
.product-card-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.5rem;
  border-top: 1px dashed #e2e8f0;
  font-size: 0.9rem;
  color: #475569;
}

.product-card-total strong {
  font-size: 1.1rem;
  color: #0d9488;
}

/* کارت فاکتور / خلاصه سفارش */
.summary-card {
  top: 1.5rem;
  background: #ffffff;
}

.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.85rem;
  font-size: 0.92rem;
  color: #475569;
}

.summary-row strong {
  color: #1e293b;
}

.summary-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 1.25rem;
}

.summary-total strong {
  font-size: 1.25rem;
  color: #0d9488;
}

/* واکنش‌گرایی برای موبایل */
@media (max-width: 575.98px) {
  .product-cart-card {
    padding: 1rem;
  }

  .product-card-image {
    width: 75px;
    height: 75px;
    min-width: 75px;
  }

  .product-card-title {
    font-size: 0.92rem;
  }

  .product-card-details {
    grid-template-columns: 1fr 1fr;
    gap: 0.6rem 0.5rem;
    padding: 0.6rem 0.75rem;
  }

  .product-card-total strong {
    font-size: 1rem;
  }

  .summary-card .card-body {
    padding: 1.25rem;
  }
}
</style>
